import { z } from 'zod';
import { disputeTextSchema } from './current-dispute';
import { deliveryPreferenceSchema } from './delivery-preference';
import { kvnAmountSchema } from './current-finance';
import { skillRequestPayloadSchema } from './current-skill-request';
import {
  entityIdSchema,
  milestoneSchema,
  taskSchema,
  planningQuoteSchema,
  u32,
  evidenceSchema,
  scoreSchema,
} from './current-project';

const revision = z
  .object({ expectedRevision: z.number().int().nonnegative().safe() })
  .strict();
const projectId = entityIdSchema.transform(
  (id) => '0x' + id.replace(/^0x/, '').toLowerCase()
);
const nonblank = z
  .string()
  .refine((value) => /\S/.test(value), 'Enter nonblank text.');
const uniqueKeys = (items: { key: number }[]) =>
  new Set(items.map((item) => item.key)).size === items.length;
const definition = milestoneSchema.shape.definition;
const requirementSchema = definition.shape.requirements.element
  .extend({
    skillIds: z
      .array(u32.min(1))
      .min(1)
      .refine(
        (ids) => new Set(ids).size === ids.length,
        'Skills must be unique.'
      ),
  })
  .strict();
export const proposalDefinitionSchema = z
  .object({
    title: nonblank,
    description: z.string(),
    milestones: z
      .array(
        definition
          .extend({
            title: nonblank,
            requirements: z
              .array(requirementSchema)
              .refine(uniqueKeys, 'Staffing keys must be unique.'),
          })
          .strict()
      )
      .min(1)
      .refine(uniqueKeys, 'Milestone keys must be unique.'),
  })
  .strict();
export const taskDefinitionSchema = taskSchema.shape.task
  .extend({
    title: nonblank,
    assignees: z
      .array(taskSchema.shape.task.shape.assignees.element)
      .refine(
        (ids) => new Set(ids).size === ids.length,
        'Assignees must be unique.'
      ),
  })
  .strict();
export type ProposalDefinition = z.infer<typeof proposalDefinitionSchema>;
export type TaskDefinition = z.infer<typeof taskDefinitionSchema>;
export const projectCommandSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('project-evaluate'),
      projectId,
      payload: z
        .object({
          ratings: z.array(
            z
              .object({
                account: z.string().regex(/^0x[0-9a-f]{64}$/),
                score: scoreSchema,
              })
              .strict()
          ),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('proposal-delivery'),
      projectId,
      proposalId: projectId,
      payload: revision.extend({ delivery: deliveryPreferenceSchema }).strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('proposal-withdraw'),
      projectId,
      proposalId: projectId,
      payload: revision,
    })
    .strict(),
  z
    .object({
      kind: z.literal('task-delete'),
      projectId,
      storageId: projectId,
      taskId: u32,
      payload: revision,
    })
    .strict(),
  z
    .object({
      kind: z.literal('skill-request'),
      payload: skillRequestPayloadSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('bramp-deposit'),
      payload: z.object({ amount: kvnAmountSchema }).strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('bramp-withdrawal'),
      payload: z.object({ amount: kvnAmountSchema }).strict(),
    })
    .strict(),
  z.object({ kind: z.literal('bramp-confirm'), requestId: projectId }).strict(),
  z.object({ kind: z.literal('bramp-cancel'), requestId: projectId }).strict(),
  z
    .object({
      kind: z.literal('planning-dispute'),
      projectId,
      payload: z.object({ reason: nonblank }).strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('dispute-open'),
      projectId,
      payload: z
        .object({
          milestoneId: projectId,
          rejectedSubmissionId: projectId,
          reason: disputeTextSchema,
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('dispute-response'),
      projectId,
      disputeId: projectId,
      payload: z.object({ evidence: evidenceSchema }).strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('task-progress'),
      projectId,
      storageId: projectId,
      taskId: u32,
      payload: z
        .object({
          status: taskSchema.shape.task.shape.status,
          loggedMinutes: u32,
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('completion-submit'),
      projectId,
      milestoneId: projectId,
      payload: z
        .object({
          deliverable: evidenceSchema.nullable(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('completion-accept'),
      projectId,
      milestoneId: projectId,
      payload: z
        .object({
          submissionId: projectId,
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('completion-reject'),
      projectId,
      submissionId: projectId,
      payload: z.object({ commentId: projectId }).strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('create'),
      payload: z
        .object({
          title: z
            .string()
            .refine((value) => /\S/.test(value), 'Enter a project title.'),
          description: z.string(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('quote'),
      projectId,
      payload: planningQuoteSchema.strict(),
    })
    .strict(),
  z
    .object({ kind: z.literal('accept'), projectId, payload: revision })
    .strict(),
  z
    .object({
      kind: z.literal('accept-delivery'),
      projectId,
      payload: revision,
    })
    .strict(),
  z
    .object({
      kind: z.literal('cancel'),
      projectId,
      payload: z
        .object({
          reason: z
            .string()
            .refine(
              (value) => /\S/.test(value),
              'Enter a cancellation reason.'
            ),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('proposal-create'),
      projectId,
      payload: proposalDefinitionSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('proposal-update'),
      projectId,
      proposalId: projectId,
      payload: proposalDefinitionSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('proposal-delete'),
      projectId,
      proposalId: projectId,
    })
    .strict(),
  z
    .object({
      kind: z.literal('proposal-submit'),
      projectId,
      proposalId: projectId,
    })
    .strict(),
  z
    .object({
      kind: z.literal('proposal-approve'),
      projectId,
      proposalId: projectId,
      payload: revision,
    })
    .strict(),
  z
    .object({
      kind: z.literal('proposal-changes'),
      projectId,
      proposalId: projectId,
      payload: z.object({ reference: nonblank }).strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('task-create'),
      projectId,
      storageId: projectId,
      payload: taskDefinitionSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('task-update'),
      projectId,
      storageId: projectId,
      taskId: u32,
      payload: taskDefinitionSchema,
    })
    .strict(),
]);
export type ProjectCommand = z.infer<typeof projectCommandSchema>;

// Account-command namespaces are local storage keys, never backend identifiers.
export function commandScope(command: ProjectCommand) {
  return 'projectId' in command
    ? command.projectId
    : command.kind === 'create'
      ? undefined
      : command.kind === 'skill-request'
        ? 'skill-requests'
        : 'bramp';
}

export function validateProjectCommand(command: ProjectCommand) {
  if (command.kind === 'quote') validatePlanningWindow(command.payload.window);
  if (
    command.kind === 'proposal-create' ||
    command.kind === 'proposal-update'
  ) {
    let total = 0n;
    for (const milestone of command.payload.milestones) {
      validatePlanningWindow(milestone.window);
      total +=
        BigInt(milestone.coordinatorFee) +
        milestone.requirements.reduce((sum, r) => sum + BigInt(r.budget), 0n);
    }
    if (total > 18446744073709551615n)
      throw new Error('The total proposal budget exceeds supported KVN units.');
  }
}

// Native week inputs use positive four-digit years. Dec 28 is always in the last ISO week.
export function parsePlanningWeek(value: string) {
  const match = /^(\d{4})-W(\d{2})$/.exec(value);
  if (!match) throw new Error('Enter a week as YYYY-Www.');
  const isoYear = Number(match[1]);
  const week = Number(match[2]);
  const dec28 = new Date(0);
  dec28.setUTCFullYear(isoYear, 11, 28);
  const day = dec28.getUTCDay() || 7;
  dec28.setUTCDate(dec28.getUTCDate() + 4 - day);
  const jan1 = new Date(0);
  jan1.setUTCFullYear(isoYear, 0, 1);
  const lastWeek = Math.ceil(
    ((dec28.getTime() - jan1.getTime()) / 86400000 + 1) / 7
  );
  if (isoYear < 1 || week < 1 || week > lastWeek)
    throw new Error('Choose a valid ISO week.');
  return { isoYear, week };
}
export function validatePlanningWindow(
  window: z.infer<typeof planningQuoteSchema>['window']
) {
  const format = (week: typeof window.start) =>
    String(week.isoYear).padStart(4, '0') +
    '-W' +
    String(week.week).padStart(2, '0');
  parsePlanningWeek(format(window.start));
  parsePlanningWeek(format(window.end));
  if (
    window.start.isoYear > window.end.isoYear ||
    (window.start.isoYear === window.end.isoYear &&
      window.start.week > window.end.week)
  )
    throw new Error('The ending week must be on or after the starting week.');
}
